<!-- Approval Workflow Dashboard -->
<div class="approval-workflow-container">
  <!-- Header -->
  <div class="workflow-header">
    <div class="header-content">
      <h1 class="workflow-title">
        <mat-icon class="title-icon">approval</mat-icon>
        Approval Workflow Management
      </h1>
      <p class="workflow-subtitle">Manage project stage transitions and approval requests</p>
    </div>
    <div class="header-actions">
      <button mat-raised-button color="primary" (click)="openRequestDialog()">
        <mat-icon>add</mat-icon>
        New Request
      </button>
      <button mat-raised-button color="accent" (click)="loadApprovalRequests()">
        <mat-icon>refresh</mat-icon>
        Refresh
      </button>
    </div>
  </div>

  <!-- Statistics Cards -->
  <div class="stats-section" *ngIf="workflowStats">
    <div class="stats-grid">
      <div class="stat-card pending">
        <div class="stat-icon">
          <mat-icon>schedule</mat-icon>
        </div>
        <div class="stat-content">
          <h3>Pending Approvals</h3>
          <p class="stat-value">{{ workflowStats.pending_approvals }}</p>
        </div>
      </div>

      <div class="stat-card requests">
        <div class="stat-icon">
          <mat-icon>assignment</mat-icon>
        </div>
        <div class="stat-content">
          <h3>My Requests</h3>
          <p class="stat-value">{{ workflowStats.my_requests }}</p>
        </div>
      </div>

      <div class="stat-card approved">
        <div class="stat-icon">
          <mat-icon>check_circle</mat-icon>
        </div>
        <div class="stat-content">
          <h3>Approved</h3>
          <p class="stat-value">{{ workflowStats.approved_requests }}</p>
        </div>
      </div>

      <div class="stat-card rejected">
        <div class="stat-icon">
          <mat-icon>cancel</mat-icon>
        </div>
        <div class="stat-content">
          <h3>Rejected</h3>
          <p class="stat-value">{{ workflowStats.rejected_requests }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Filters and Search -->
  <div class="filters-section">
    <div class="filters-content">
      <div class="search-box">
        <div class="flex flex-row gap-4">
            <mat-label>Search Requests</mat-label>
            <mat-form-field appearance="outline" class="search-field">
          <input matInput [(ngModel)]="searchTerm" (keyup.enter)="onSearch()" placeholder="Search by project name...">
          <mat-icon matSuffix>search</mat-icon>
        </mat-form-field>
        </div>
      </div>

      <div class="filter-controls">
        <div class="flex flex-col">
            <mat-label>Status Filter</mat-label>
       
            <mat-form-field appearance="outline" class="filter-field">
          <mat-select [(ngModel)]="statusFilter" (selectionChange)="onStatusFilterChange()">
            <mat-option value="">All Status</mat-option>
            <mat-option value="pending">Pending</mat-option>
            <mat-option value="approved">Approved</mat-option>
            <mat-option value="rejected">Rejected</mat-option>
            <mat-option value="cancelled">Cancelled</mat-option>
          </mat-select>
        </mat-form-field>
      </div>
    </div>
  </div>

  <!-- Approval Requests Table -->
  <div class="requests-section">
    <div class="section-header">
      <h2>Approval Requests</h2>
      <div class="section-actions">
        <button mat-stroked-button (click)="loadApprovalRequests()">
          <mat-icon>refresh</mat-icon>
          Refresh
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div class="loading-state" *ngIf="loadingRequests">
      <mat-spinner diameter="40"></mat-spinner>
      <p>Loading approval requests...</p>
    </div>

    <!-- Requests Table -->
    <div class="requests-table" *ngIf="!loadingRequests">
      <table mat-table [dataSource]="approvalRequests" class="mat-elevation-z1">
        <!-- Project Column -->
        <ng-container matColumnDef="project">
          <th mat-header-cell *matHeaderCellDef>Project</th>
          <td mat-cell *matCellDef="let request">
            <div class="project-info">
              <h4>{{ request.project_name }}</h4>
              <p>{{ request.project_description }}</p>
            </div>
          </td>
        </ng-container>

        <!-- Stage Transition Column -->
        <ng-container matColumnDef="transition">
          <th mat-header-cell *matHeaderCellDef>Stage Transition</th>
          <td mat-cell *matCellDef="let request">
            <div class="transition-info">
              <span class="from-stage">{{ request.from_stage_name }}</span>
              <mat-icon class="arrow">arrow_forward</mat-icon>
              <span class="to-stage">{{ request.to_stage_name }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Requester Column -->
        <ng-container matColumnDef="requester">
          <th mat-header-cell *matHeaderCellDef>Requested By</th>
          <td mat-cell *matCellDef="let request">
            <div class="user-info">
              <mat-icon class="user-icon">person</mat-icon>
              <span>{{ request.requester_name }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Status Column -->
        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>Status</th>
          <td mat-cell *matCellDef="let request">
            <div class="status-badge" [ngClass]="getStatusClass(request.status)">
              <mat-icon>{{ getStatusIcon(request.status) }}</mat-icon>
              <span>{{ getStatusText(request.status) }}</span>
            </div>
          </td>
        </ng-container>

        <!-- Date Column -->
        <ng-container matColumnDef="date">
          <th mat-header-cell *matHeaderCellDef>Requested Date</th>
          <td mat-cell *matCellDef="let request">
            {{ formatDate(request.requested_at) }}
          </td>
        </ng-container>

        <!-- Actions Column -->
        <ng-container matColumnDef="actions">
          <th mat-header-cell *matHeaderCellDef>Actions</th>
          <td mat-cell *matCellDef="let request">
            <div class="action-buttons">
              <button mat-icon-button color="primary" 
                      (click)="openHistoryDialog(request)"
                      matTooltip="View History">
                <mat-icon>history</mat-icon>
              </button>

              <button mat-icon-button color="accent" 
                      *ngIf="canApprove(request)"
                      (click)="openApprovalDialog(request)"
                      matTooltip="Process Approval">
                <mat-icon>approval</mat-icon>
              </button>

              <button mat-icon-button color="warn" 
                      *ngIf="canCancel(request)"
                      (click)="cancelApprovalRequest(request)"
                      matTooltip="Cancel Request">
                <mat-icon>cancel</mat-icon>
              </button>
            </div>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="['project', 'transition', 'requester', 'status', 'date', 'actions']"></tr>
        <tr mat-row *matRowDef="let row; columns: ['project', 'transition', 'requester', 'status', 'date', 'actions'];"></tr>
      </table>

      <!-- Empty State -->
      <div class="empty-state" *ngIf="approvalRequests.length === 0">
        <mat-icon class="empty-icon">inbox</mat-icon>
        <h3>No approval requests found</h3>
        <p>There are no approval requests matching your current filters.</p>
        <button mat-raised-button color="primary" (click)="openRequestDialog()">
          <mat-icon>add</mat-icon>
          Create New Request
        </button>
      </div>
    </div>

    <!-- Pagination -->
    <div class="pagination-section" *ngIf="totalPages > 1">
      <mat-paginator 
        [length]="totalItems"
        [pageSize]="itemsPerPage"
        [pageIndex]="currentPage - 1"
        [pageSizeOptions]="[5, 10, 25, 50]"
        (page)="onPageChange($event.pageIndex + 1)">
      </mat-paginator>
    </div>
  </div>
</div>

<!-- Approval Dialog -->
<div class="dialog-overlay" *ngIf="showApprovalDialog" (click)="closeApprovalDialog()">
  <div class="dialog-content" (click)="$event.stopPropagation()">
    <div class="dialog-header">
      <h2>Process Approval Request</h2>
      <button mat-icon-button (click)="closeApprovalDialog()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="dialog-body" *ngIf="selectedRequest">
      <div class="request-details">
        <h3>Request Details</h3>
        <p><strong>Project:</strong> {{ selectedRequest.project_name }}</p>
        <p><strong>From Stage:</strong> {{ selectedRequest.from_stage_name }}</p>
        <p><strong>To Stage:</strong> {{ selectedRequest.to_stage_name }}</p>
        <p><strong>Requested By:</strong> {{ selectedRequest.requester_name }}</p>
        <p><strong>Reason:</strong> {{ selectedRequest.request_reason }}</p>
      </div>

      <form [formGroup]="approvalForm" class="approval-form">
      <div class="flex flex-col"> 
        <mat-label>Action</mat-label>
        <mat-form-field appearance="outline" class="full-width">
          <mat-select formControlName="action">
            <mat-option value="approve">Approve</mat-option>
            <mat-option value="reject">Reject</mat-option>
          </mat-select>
        </mat-form-field>
</div>
<div class="flex flex-col"> 
    <mat-label>Notes</mat-label>
    <mat-form-field appearance="outline" class="full-width">
          <textarea matInput formControlName="notes" rows="4" 
                    placeholder="Enter approval notes or feedback..."></textarea>
        </mat-form-field>
</div>      </form>
    </div>

    <div class="dialog-actions">
      <button mat-button (click)="closeApprovalDialog()">Cancel</button>
      <button mat-raised-button color="primary" 
              [disabled]="!approvalForm.valid"
              (click)="processApproval()">
        Process Approval
      </button>
    </div>
  </div>
</div>

<!-- New Request Dialog -->
<div class="dialog-overlay" *ngIf="showRequestDialog" (click)="closeRequestDialog()">
  <div class="dialog-content" (click)="$event.stopPropagation()">
    <div class="dialog-header">
      <h2>Create Approval Request</h2>
      <button mat-icon-button (click)="closeRequestDialog()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="dialog-body">
      <form [formGroup]="requestForm" class="request-form">
        <div class="flex flex-col"> 
            <mat-label>Project</mat-label>

<!-- 
            <mat-form-field appearance="outline" class="full-width">
              <input matInput 
                     [formControlName]="'projectId'"
                     [matAutocomplete]="projectAuto"
                     placeholder="Search for a project..."
                     (input)="onProjectSearchChange($any($event.target).value)">
              <mat-icon matSuffix *ngIf="isProjectSearching">
                <mat-spinner diameter="20"></mat-spinner>
              </mat-icon>
              <mat-icon matSuffix *ngIf="!isProjectSearching && projectSearchTerm">
                search
              </mat-icon>
            </mat-form-field> -->
                 <div class="col-span-6">
          <!-- <mat-label>{{ "CLIENT_NAME" | translate }}</mat-label> -->

          <mat-form-field appearance="outline">
            <input
              type="text"
              matInput
              [matAutocomplete]="auto"
              formControlName="client_name"
            />
            <mat-autocomplete
              #auto="matAutocomplete"
              (optionSelected)="onProjectSelected($event)"
            >
              <mat-option
                *ngFor="let project of filteredProjects"
                [value]="project.project_name"
              >
                {{ project.project_name }}
              </mat-option>
            </mat-autocomplete>
          </mat-form-field>
        </div>
            <mat-autocomplete #projectAuto="matAutocomplete" 
                             [displayWith]="getProjectDisplayText.bind(this)"
                             (optionSelected)="onProjectSelected($event)">
              <mat-option *ngFor="let project of getDisplayProjects()" [value]="project">
                <div class="project-option">
                  <div class="project-name">{{ project.project_name }}</div>
                  <div class="project-details">
                    <span class="project-id">{{ project.project_identifier }}</span>
                    <span class="project-client">{{ project.client_name }}</span>
                    <span class="project-stage">{{ project.stage }}</span>
                  </div>
                </div>
              </mat-option>
              <mat-option *ngIf="getDisplayProjects().length === 0 && !isProjectSearching" disabled>
                No projects found
              </mat-option>
              <mat-option *ngIf="isProjectSearching" disabled>
                <mat-spinner diameter="20"></mat-spinner>
                Searching...
              </mat-option>
            </mat-autocomplete>
        </div>
        <div class="flex flex-col">  
            <mat-label>Target Stage</mat-label>
        
            <mat-form-field appearance="outline" class="full-width">
          <mat-select formControlName="toStageId">
            <mat-option *ngFor="let stage of projectStages" [value]="stage.id">
              {{ stage.name }}
               <!-- - {{ stage.description }} -->
            </mat-option>
          </mat-select>
        </mat-form-field>
</div>
        <div class="flex flex-col">  
            <mat-label>Request Reason</mat-label>
                <mat-form-field appearance="outline" class="full-width">
          
          <textarea matInput formControlName="requestReason" rows="4" 
                    placeholder="Explain why you need to move to this stage..."></textarea>
        </mat-form-field>
        </div>
      </form>
    </div>

    <div class="dialog-actions">
      <button mat-button (click)="closeRequestDialog()">Cancel</button>
      <button mat-raised-button color="primary" 
              [disabled]="!requestForm.valid"
              (click)="createApprovalRequest()">
        Create Request
      </button>
    </div>
  </div>
</div>

<!-- History Dialog -->
<div class="dialog-overlay" *ngIf="showHistoryDialog" (click)="closeHistoryDialog()">
  <div class="dialog-content" (click)="$event.stopPropagation()">
    <div class="dialog-header">
      <h2>Workflow History</h2>
      <button mat-icon-button (click)="closeHistoryDialog()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <div class="dialog-body" *ngIf="selectedRequest">
      <div class="request-summary">
        <h3>{{ selectedRequest.project_name }}</h3>
        <p>{{ selectedRequest.from_stage_name }} → {{ selectedRequest.to_stage_name }}</p>
      </div>

      <div class="history-timeline" *ngIf="selectedRequest.history">
        <div class="timeline-item" *ngFor="let item of selectedRequest.history">
          <div class="timeline-icon" [ngClass]="'action-' + item.action">
            <mat-icon>{{ getActionIcon(item.action) }}</mat-icon>
          </div>
          <div class="timeline-content">
            <h4>{{ getActionText(item.action) }}</h4>
            <p><strong>{{ item.actor_name }}</strong> ({{ item.actor_role }})</p>
            <p *ngIf="item.notes">{{ item.notes }}</p>
            <small>{{ formatDate(item.created_at) }}</small>
          </div>
        </div>
      </div>
    </div>

    <div class="dialog-actions">
      <button mat-button (click)="closeHistoryDialog()">Close</button>
    </div>
  </div>
</div>

